// src/db/core/auth.js
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AUTHENTICATION OPERATIONS (SUPABASE AUTH)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Handles all authentication flows:
 * - Email/Password (with email verification)
 * - OAuth (Google, Microsoft)
 * - Password reset
 * - Session management
 *
 * PRODUCTION READY:
 * ✅ Email verification required for new signups
 * ✅ Password reset via email
 * ✅ OAuth providers (Google, Microsoft)
 * ✅ Secure session handling
 * ✅ Error handling
 *
 * SUPABASE HANDLES:
 * ✅ Password hashing (bcrypt)
 * ✅ Email sending (verification, reset)
 * ✅ OAuth callbacks
 * ✅ Token refresh
 * ✅ Rate limiting
 * ✅ Security (breach detection, etc.)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { supabase } from './supabase.js';

// ══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get redirect URL for OAuth and email flows
 * Uses window.location.origin if available, falls back to configured URL
 */
function getRedirectURL(path = '/billing') {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  // Fallback for server-side (update with your production URL)
  return `http://localhost:3000${path}`;
}

// ══════════════════════════════════════════════════════════════════════════════
// SIGN UP
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Create new user account with email/password
 * 
 * FLOW:
 * 1. User submits email + password
 * 2. Supabase creates user (password hashed automatically)
 * 3. Supabase sends verification email
 * 4. User clicks link in email → email verified
 * 5. User can now sign in
 *
 * SECURITY:
 * ✅ Email verification REQUIRED (configured in Supabase Dashboard)
 * ✅ Password hashed with bcrypt (Supabase handles this)
 * ✅ User cannot sign in until email verified
 *
 * @param {string} email - User's email address
 * @param {string} password - User's password (min 6 characters)
 * @returns {Promise<{user, session}>}
 */
export async function signUpWithEmail(email, password) {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: getRedirectURL('/billing')
      }
    });

    if (error) throw error;
    
    // Check if email confirmation is required
    if (data.user && !data.session) {
      console.log('✓ Account created - verification email sent to:', email);
      console.log('⚠️  User must verify email before signing in');
    }
    
    return data;
  } catch (error) {
    console.error('Sign up error:', error);
    throw error;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// SIGN IN
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Sign in with email/password
 * 
 * REQUIREMENTS:
 * - Email must be verified (if email verification enabled)
 * - Correct password
 *
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<{user, session}>}
 */
export async function signInWithEmail(email, password) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    
    console.log('✓ User signed in:', data.user.email);
    return data;
  } catch (error) {
    console.error('Sign in error:', error);
    throw error;
  }
}

/**
 * Sign in with Google OAuth
 * 
 * FLOW:
 * 1. User clicks "Sign in with Google"
 * 2. Redirects to Google login
 * 3. User authorizes app
 * 4. Google redirects back to your app
 * 5. User is signed in
 *
 * SETUP REQUIRED:
 * - Google OAuth app created in Google Cloud Console
 * - Client ID/Secret added to Supabase Dashboard
 * - Redirect URI configured
 *
 * @returns {Promise}
 */
export async function signInWithGoogle() {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getRedirectURL('/billing')
      }
    });

    if (error) throw error;
    
    console.log('✓ Redirecting to Google sign in...');
    return data;
  } catch (error) {
    console.error('Google sign in error:', error);
    throw error;
  }
}

/**
 * Sign in with Microsoft OAuth
 * 
 * FLOW:
 * 1. User clicks "Sign in with Microsoft"
 * 2. Redirects to Microsoft login
 * 3. User authorizes app
 * 4. Microsoft redirects back to your app
 * 5. User is signed in
 *
 * SETUP REQUIRED:
 * - Azure app registration created in Azure Portal
 * - Application ID/Secret added to Supabase Dashboard
 * - Redirect URI configured
 *
 * @returns {Promise}
 */
export async function signInWithMicrosoft() {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: getRedirectURL('/billing'),
        scopes: 'email'
      }
    });

    if (error) throw error;
    
    console.log('✓ Redirecting to Microsoft sign in...');
    return data;
  } catch (error) {
    console.error('Microsoft sign in error:', error);
    throw error;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// SIGN OUT
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Sign out current user
 * Clears session and tokens
 */
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    
    // ═══════════════════════════════════════════════════════════════════
    // CLEAR ALL SESSION STORAGE (Free Tier Data)
    // ═══════════════════════════════════════════════════════════════════
    try {
      sessionStorage.removeItem('invoice_gen_products_session');
      sessionStorage.removeItem('invoice_gen_customers_session');
      sessionStorage.removeItem('invoice_gen_invoices_session');
      console.log('✓ Session storage cleared');
    } catch (storageError) {
      console.warn('Failed to clear session storage:', storageError);
    }
    
    console.log('✓ User signed out');
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// PASSWORD RESET
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Send password reset email
 * 
 * FLOW (UI will implement this later):
 * 1. User clicks "Forgot Password?"
 * 2. User enters email
 * 3. Call this function
 * 4. Supabase sends email with reset link
 * 5. User clicks link → redirected to /reset-password page
 * 6. User enters new password
 * 7. Call updatePassword()
 *
 * SUPABASE HANDLES:
 * ✅ Email generation
 * ✅ Email sending
 * ✅ Reset token generation
 * ✅ Token expiration (1 hour default)
 *
 * @param {string} email - User's email address
 * @returns {Promise<void>}
 */
export async function sendPasswordResetEmail(email) {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getRedirectURL('/reset-password')
    });

    if (error) throw error;
    
    console.log('✓ Password reset email sent to:', email);
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
}

/**
 * Update user's password
 * 
 * USAGE:
 * - Called on /reset-password page after user clicks email link
 * - User is already authenticated via reset token (from email)
 * - Just need to provide new password
 *
 * @param {string} newPassword - New password (min 6 characters)
 * @returns {Promise<void>}
 */
export async function updatePassword(newPassword) {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
    
    console.log('✓ Password updated successfully');
  } catch (error) {
    console.error('Update password error:', error);
    throw error;
  }
}

/**
 * Resend email verification
 * 
 * USAGE:
 * - User didn't receive verification email
 * - User's email verification expired
 *
 * @param {string} email - User's email
 * @returns {Promise<void>}
 */
export async function resendVerificationEmail(email) {
  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email
    });

    if (error) throw error;
    
    console.log('✓ Verification email resent to:', email);
  } catch (error) {
    console.error('Resend verification error:', error);
    throw error;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// USER & SESSION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get current authenticated user
 * @returns {Promise<User|null>}
 */
export async function getCurrentUser() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  } catch (error) {
    console.error('Get user error:', error);
    return null;
  }
}

/**
 * Get current session
 * @returns {Promise<Session|null>}
 */
export async function getSession() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  } catch (error) {
    console.error('Get session error:', error);
    return null;
  }
}

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  const session = await getSession();
  return session !== null;
}

/**
 * Get user ID of currently logged in user
 * @returns {Promise<string|null>}
 */
export async function getCurrentUserId() {
  const user = await getCurrentUser();
  return user?.id || null;
}

// ══════════════════════════════════════════════════════════════════════════════
// AUTH STATE LISTENER
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Listen to authentication state changes
 * 
 * USAGE (in app.js or root component):
 * ```javascript
 * onAuthStateChange((event, session) => {
 *   if (event === 'SIGNED_IN') {
 *     console.log('User signed in:', session.user);
 *   }
 *   if (event === 'SIGNED_OUT') {
 *     console.log('User signed out');
 *   }
 * });
 * ```
 *
 * EVENTS:
 * - SIGNED_IN: User just signed in
 * - SIGNED_OUT: User just signed out
 * - TOKEN_REFRESHED: Session token refreshed
 * - USER_UPDATED: User data updated
 * - PASSWORD_RECOVERY: User clicked password reset link
 *
 * @param {Function} callback - Called with (event, session)
 * @returns {Object} Subscription object (call .unsubscribe() to stop listening)
 */
export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange(callback);
}

// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY
// ══════════════════════════════════════════════════════════════════════════════

/**
 * EXPORTED FUNCTIONS (13 total):
 *
 * ✅ Sign Up:
 * - signUpWithEmail(email, password) - Create account with email verification
 *
 * ✅ Sign In:
 * - signInWithEmail(email, password) - Sign in with password
 * - signInWithGoogle() - OAuth with Google
 * - signInWithMicrosoft() - OAuth with Microsoft
 *
 * ✅ Sign Out:
 * - signOut() - End session
 *
 * ✅ Password Reset:
 * - sendPasswordResetEmail(email) - Send reset link via email
 * - updatePassword(newPassword) - Set new password
 * - resendVerificationEmail(email) - Resend verification email
 *
 * ✅ User & Session:
 * - getCurrentUser() - Get user object
 * - getSession() - Get session object
 * - isAuthenticated() - Check if user signed in
 * - getCurrentUserId() - Get user ID
 *
 * ✅ State Management:
 * - onAuthStateChange(callback) - Listen to auth events
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SUPABASE HANDLES AUTOMATICALLY:
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ✅ Password hashing (bcrypt with salt)
 * ✅ Email verification (sends email, validates token)
 * ✅ Password reset (sends email, validates token)
 * ✅ OAuth callbacks (Google, Microsoft redirects)
 * ✅ Session management (JWT tokens)
 * ✅ Token refresh (automatic)
 * ✅ Rate limiting (prevents brute force)
 * ✅ Security (breach detection, IP tracking)
 *
 * YOU IMPLEMENT:
 * - UI pages (/login, /signup, /forgot-password, /reset-password)
 * - Call these functions from UI
 * - Handle success/error states
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */